import React, { useState, useEffect, useCallback, useRef } from 'react';
import { schedulesApi, teamsApi, shiftSwapsApi, usersApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CaretLeft, CaretRight, House, Briefcase, Phone, CalendarX, User, Users, Clock, FilePdf, ArrowsClockwise, Trophy } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const TYPE_ICONS = {
  office: { Icon: House, color: '#22C55E' },
  smart_working: { Icon: Briefcase, color: '#002FA7' },
  on_call: { Icon: Phone, color: '#FF2A00' },
  leave: { Icon: CalendarX, color: '#F59E0B' },
  permission: { Icon: Clock, color: '#8B5CF6' },
  overtime: { Icon: Clock, color: '#059669' },
};
const TYPE_LABELS = {
  office: 'Ufficio', smart_working: 'Smart Working', on_call: 'Reperibilità',
  leave: 'Ferie/Assenza', permission: 'Permesso', overtime: 'Straordinario',
};
const GRID_LABELS = {
  office: 'Presente', smart_working: 'Smart Working', on_call: 'Reperibilità',
  leave: 'Ferie/Assenza', permission: 'Permesso', overtime: 'Straordinario',
};
const GRID_BG = {
  office: '#22C55E', smart_working: '#002FA7', on_call: '#FF2A00',
  leave: '#F59E0B', permission: '#8B5CF6', overtime: '#059669',
};
const GRID_SHORT = {
  office: 'P', smart_working: 'SW', on_call: 'R',
  leave: 'F', permission: 'PE', overtime: 'S',
};
const GRID_PRIORITY = ['on_call', 'leave', 'permission', 'overtime', 'smart_working', 'office'];

// Red shades for multiple on-call rotation groups
const ON_CALL_COLORS = ['#FF2A00', '#E11D48', '#BE123C', '#9F1239', '#881337', '#7F1D1D'];

const getPrimaryType = (types) => {
  for (const p of GRID_PRIORITY) {
    if (types.includes(p)) return p;
  }
  return types[0] || 'office';
};

const ScheduleIcon = ({ type }) => {
  const { Icon, color } = TYPE_ICONS[type] || TYPE_ICONS.office;
  return <Icon size={14} weight="bold" style={{ color }} />;
};

const LOGO_URL = '/logo-s2e.png';

const CalendarPage = () => {
  const { user, isTeamLeader, isManager } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [calendarView, setCalendarView] = useState('team');
  const [displayMode, setDisplayMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [logoBase64, setLogoBase64] = useState('');
  const gridRef = useRef(null);
  // Swap state (personal view)
  const [isSwapOpen, setIsSwapOpen] = useState(false);
  const [swapSchedule, setSwapSchedule] = useState(null);
  const [colleagues, setColleagues] = useState([]);
  const [swapTargetId, setSwapTargetId] = useState('');
  const [swapReason, setSwapReason] = useState('');
  const [swapping, setSwapping] = useState(false);
  // Day detail dialog
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [dayDetailData, setDayDetailData] = useState(null);
  const [dayDetailLoading, setDayDetailLoading] = useState(false);
  const [dayDetailUser, setDayDetailUser] = useState(null);
  // On-call tab
  const [calendarTab, setCalendarTab] = useState('general'); // 'general' | 'oncall'

  // Convert logo to base64 for display
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      setLogoBase64(canvas.toDataURL('image/png'));
    };
    img.src = '/logo-s2e.png';
  }, []);

  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const teamId = selectedTeam === 'all' ? undefined : selectedTeam;
      const { data } = await schedulesApi.getCalendar(month, year, teamId, calendarView);
      setSchedules(data);
    } catch (error) { console.error('Errore:', error); } finally { setLoading(false); }
  }, [month, year, selectedTeam, calendarView]);

  const fetchTeams = useCallback(async () => {
    try { const { data } = await teamsApi.getAll(); setTeams(data); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);
  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const getDaysInMonth = (y, m) => new Date(y, m, 0).getDate();
  const getFirstDayOfMonth = (y, m) => { const d = new Date(y, m - 1, 1).getDay(); return d === 0 ? 6 : d - 1; };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push({ day: null, isCurrentMonth: false });
    for (let i = 1; i <= daysInMonth; i++) days.push({ day: i, isCurrentMonth: true });
    const totalCells = Math.ceil(days.length / 7) * 7;
    for (let i = days.length; i < totalCells; i++) days.push({ day: null, isCurrentMonth: false });
    return days;
  };

  const getSchedulesForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return schedules.filter((s) => s.start_date <= dateStr && s.end_date >= dateStr);
  };

  const isWeekend = (day) => {
    if (!day) return false;
    const d = new Date(year, month - 1, day);
    return d.getDay() === 0 || d.getDay() === 6;
  };
  const calendarDays = generateCalendarDays();

  const openSwapDialog = async (schedule) => {
    setSwapSchedule(schedule);
    setSwapTargetId('');
    setSwapReason('');
    try {
      const { data } = await usersApi.getSameBl();
      setColleagues(data.filter(u => u.id !== user.id));
    } catch { setColleagues([]); }
    setIsSwapOpen(true);
  };

  const handleSwap = async () => {
    if (!swapSchedule || !swapTargetId) return;
    setSwapping(true);
    try {
      await shiftSwapsApi.create({ schedule_id: swapSchedule.id, target_user_id: swapTargetId, reason: swapReason });
      toast.success('Proposta di scambio inviata');
      setIsSwapOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSwapping(false); }
  };

  const openDayDetail = async (userId, userName, day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setDayDetailUser({ name: userName, date: dateStr, day });
    setDayDetailOpen(true);
    setDayDetailLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/schedules/day-detail`, {
        params: { date: dateStr, user_id: userId }, withCredentials: true
      });
      setDayDetailData(data);
    } catch { setDayDetailData([]); }
    finally { setDayDetailLoading(false); }
  };

  // Get team leaders map from teams data
  const teamLeaderIds = new Set(teams.filter(t => t.team_leader_id).map(t => t.team_leader_id));

  // Grid: ALL days of month (including weekends for on-call)
  const buildGridData = () => {
    const daysInMonth = getDaysInMonth(year, month);
    const allDays = [];
    for (let d = 1; d <= daysInMonth; d++) allDays.push(d);
    
    // Collect unique rotation groups for on-call numbering
    const rotationIds = new Set();
    schedules.forEach(s => {
      if (s.schedule_type === 'on_call' && s.rotation_id) rotationIds.add(s.rotation_id);
    });
    // Assign non-rotation on-call schedules a pseudo group
    let pseudoGroupCounter = rotationIds.size;
    const nonRotationGroups = {};
    schedules.forEach(s => {
      if (s.schedule_type === 'on_call' && !s.rotation_id) {
        const key = s.user_id + '_' + s.start_date;
        if (!nonRotationGroups[key]) {
          pseudoGroupCounter++;
          nonRotationGroups[key] = `_pseudo_${pseudoGroupCounter}`;
        }
      }
    });
    
    // Build rotation index: rotation_id -> group number (1-based)
    const rotationIndex = {};
    let gi = 1;
    [...rotationIds].sort().forEach(rid => { rotationIndex[rid] = gi++; });
    
    const userMap = {};
    schedules.forEach(s => {
      const key = s.user_id;
      if (!userMap[key]) userMap[key] = { name: s.user_name, team_id: s.team_id, days: {}, dayMeta: {} };
      const sd = new Date(s.start_date);
      const ed = new Date(s.end_date);
      let cur = new Date(sd);
      while (cur <= ed) {
        const dayNum = cur.getDate();
        const m = cur.getMonth() + 1;
        const y2 = cur.getFullYear();
        if (m === month && y2 === year) {
          if (!userMap[key].days[dayNum]) userMap[key].days[dayNum] = [];
          if (!userMap[key].days[dayNum].includes(s.schedule_type)) {
            userMap[key].days[dayNum].push(s.schedule_type);
          }
          // Store on-call group info
          if (s.schedule_type === 'on_call') {
            const gNum = s.rotation_id ? rotationIndex[s.rotation_id] : null;
            userMap[key].dayMeta[dayNum] = { onCallGroup: gNum, rotationId: s.rotation_id };
          }
        }
        cur.setDate(cur.getDate() + 1);
      }
    });
    const users = Object.entries(userMap).map(([id, data]) => ({ id, ...data }));

    const teamLookup = {};
    teams.forEach(t => { teamLookup[t.id] = t.name; });

    const teamMap = {};
    users.forEach(u => {
      const tid = u.team_id || '_no_team';
      if (!teamMap[tid]) teamMap[tid] = [];
      teamMap[tid].push(u);
    });

    const groups = Object.entries(teamMap).map(([tid, members]) => ({
      team_id: tid,
      team_name: tid === '_no_team' ? 'Senza Team' : (teamLookup[tid] || tid),
      users: members.sort((a, b) => a.name.localeCompare(b.name)),
    })).sort((a, b) => {
      if (a.team_id === '_no_team') return 1;
      if (b.team_id === '_no_team') return -1;
      return a.team_name.localeCompare(b.team_name);
    });
    return { allDays, groups, totalRotationGroups: rotationIds.size };
  };

  const selectedTeamObj = teams.find(t => t.id === selectedTeam);

  const exportGridPdf = async () => {
    setExporting(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { applyPlugin } = await import('jspdf-autotable');
      applyPlugin(jsPDF);

      const { allDays, groups, totalRotationGroups } = buildGridData();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const margin = 6;

      // Title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(10, 10, 10);
      doc.text('Turnazione Mensile', margin, margin + 5);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      const teamLabel = selectedTeamObj
        ? `${selectedTeamObj.name}${selectedTeamObj.description ? ' - ' + selectedTeamObj.description : ''}`
        : 'Tutti i Team';
      doc.text(`${teamLabel} — ${MONTHS[month - 1].toUpperCase()} ${year}`, margin, margin + 9);

      // Legend line
      const legendItems = [
        { label: 'P = Presente', color: [34, 197, 94] },
        { label: 'SW = Smart Working', color: [0, 47, 167] },
        { label: 'R = Reperibilità', color: [255, 42, 0] },
        { label: 'F = Ferie/Assenza', color: [245, 158, 11] },
        { label: 'PE = Permesso', color: [139, 92, 246] },
        { label: 'S = Straordinario', color: [5, 150, 105] },
      ];
      let legendX = margin;
      doc.setFontSize(5.5);
      legendItems.forEach(item => {
        doc.setFillColor(...item.color);
        doc.rect(legendX, margin + 11, 2.5, 2.5, 'F');
        doc.setTextColor(82, 82, 91);
        doc.setFont('helvetica', 'bold');
        doc.text(item.label, legendX + 3.5, margin + 13.2);
        legendX += doc.getTextWidth(item.label) + 7;
      });

      // Build table header
      const headerRow1 = [{ content: 'Dipendente', styles: { halign: 'left', fontStyle: 'bold' } }];
      const headerRow2 = [{ content: '', styles: { halign: 'left' } }];
      allDays.forEach(d => {
        const dt = new Date(year, month - 1, d);
        const dayName = DAYS[dt.getDay() === 0 ? 6 : dt.getDay() - 1];
        const weekend = dt.getDay() === 0 || dt.getDay() === 6;
        headerRow1.push({
          content: dayName,
          styles: { halign: 'center', fillColor: weekend ? [244, 244, 245] : [255, 255, 255], textColor: [161, 161, 170], fontSize: 5, fontStyle: 'normal' }
        });
        headerRow2.push({
          content: String(d),
          styles: { halign: 'center', fillColor: weekend ? [244, 244, 245] : [255, 255, 255], fontStyle: 'bold', fontSize: 6 }
        });
      });

      // Build table body
      const body = [];
      const getOnCallDisplay = (dayMeta) => {
        if (!dayMeta) return { color: [255, 42, 0], label: 'R' };
        const gNum = dayMeta.onCallGroup;
        if (gNum) {
          const colors = [[255,42,0],[225,29,72],[190,18,60],[159,18,57],[136,19,55],[127,29,29]];
          return { color: colors[(gNum - 1) % colors.length], label: `R${gNum}` };
        }
        return { color: [255, 42, 0], label: 'R' };
      };

      const typeColors = {
        office: [34, 197, 94],
        smart_working: [0, 47, 167],
        on_call: [255, 42, 0],
        leave: [245, 158, 11],
        permission: [139, 92, 246],
        overtime: [5, 150, 105],
      };
      const typeShort = {
        office: 'P', smart_working: 'SW', on_call: 'R',
        leave: 'F', permission: 'PE', overtime: 'S',
      };

      // Track cells that need secondary overlay badges
      // Key: "rowIndex-colIndex" -> [{label, color}]
      const overlayMap = {};
      let bodyRowIndex = 0;

      groups.forEach(g => {
        // Team separator row
        body.push([{
          content: `${g.team_name} (${g.users.length})`,
          colSpan: allDays.length + 1,
          styles: { fillColor: [244, 244, 245], fontStyle: 'bold', fontSize: 5.5, textColor: [82, 82, 91], halign: 'left', cellPadding: { top: 1.2, bottom: 1.2, left: 2, right: 1 } }
        }]);
        bodyRowIndex++;

        g.users.forEach(u => {
          const row = [{ content: u.name, styles: { halign: 'left', fontStyle: 'normal', fontSize: 6, cellPadding: { top: 1.5, bottom: 1.5, left: 1.5, right: 1 } } }];
          allDays.forEach((d, colIdx) => {
            const dt = new Date(year, month - 1, d);
            const weekend = dt.getDay() === 0 || dt.getDay() === 6;
            const types = u.days[d];

            if (!types && weekend) {
              row.push({ content: '', styles: { fillColor: [244, 244, 245] } });
            } else if (!types) {
              row.push({
                content: 'P',
                styles: { halign: 'center', fillColor: [34, 197, 94], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.5 }
              });
            } else {
              const primary = getPrimaryType(types);
              const secondary = types.filter(t => t !== primary);
              const isOnCall = primary === 'on_call';
              const onCallInfo = isOnCall ? getOnCallDisplay(u.dayMeta?.[d]) : null;
              const bg = isOnCall ? onCallInfo.color : (typeColors[primary] || [228, 228, 231]);
              const primaryLabel = isOnCall ? onCallInfo.label : (typeShort[primary] || '?');
              row.push({
                content: primaryLabel,
                styles: { halign: 'center', fillColor: bg, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.5 }
              });
              // Store secondary overlays
              if (secondary.length > 0) {
                overlayMap[`${bodyRowIndex}-${colIdx + 1}`] = secondary.map(t => ({
                  label: typeShort[t] || '?',
                  color: typeColors[t] || [82, 82, 91],
                }));
              }
            }
          });
          body.push(row);
          bodyRowIndex++;
        });
      });

      // Compute column widths
      const availableW = pw - margin * 2;
      const nameColW = 28;
      const dayColW = (availableW - nameColW) / allDays.length;

      doc.autoTable({
        startY: margin + 15,
        margin: { left: margin, right: margin },
        head: [headerRow1, headerRow2],
        body: body,
        theme: 'grid',
        styles: {
          fontSize: 6,
          cellPadding: { top: 1.5, bottom: 1.5, left: 0.5, right: 0.5 },
          lineColor: [228, 228, 231],
          lineWidth: 0.2,
          overflow: 'hidden',
          halign: 'center',
          valign: 'middle',
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [10, 10, 10],
          lineColor: [228, 228, 231],
          lineWidth: 0.2,
          fontSize: 5.5,
          cellPadding: { top: 1, bottom: 1, left: 0.5, right: 0.5 },
          halign: 'center',
          valign: 'middle',
        },
        columnStyles: {
          0: { cellWidth: nameColW, halign: 'left' },
        },
        tableWidth: availableW,
        didParseCell: (data) => {
          // All day columns same width
          if (data.column.index > 0) {
            data.cell.styles.cellWidth = dayColW;
          }
        },
        didDrawCell: (data) => {
          if (data.section !== 'body') return;
          const key = `${data.row.index}-${data.column.index}`;
          const overlays = overlayMap[key];
          if (!overlays || overlays.length === 0) return;
          const cell = data.cell;
          overlays.forEach((ov, i) => {
            const badgeW = 3;
            const badgeH = 2;
            const bx = cell.x + cell.width - badgeW - 0.2 - (i * (badgeW + 0.3));
            const by = cell.y + 0.2;
            doc.setFillColor(...ov.color);
            doc.rect(bx, by, badgeW, badgeH, 'F');
            doc.setFontSize(3);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.text(ov.label, bx + badgeW / 2, by + 1.5, { align: 'center' });
          });
        },
      });

      // Footer
      const now = new Date();
      const ts = now.toLocaleDateString('it-IT') + ' alle ' + now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
      const finalY = doc.lastAutoTable?.finalY || ph - 10;
      doc.setFontSize(5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(160, 160, 160);
      doc.text(`generato il ${ts} con S2E DCO Planner`, pw - margin, Math.min(finalY + 6, ph - 3), { align: 'right' });
      doc.text('S2E DCO PLANNER', margin, Math.min(finalY + 6, ph - 3));

      doc.save(`Turnazione_${MONTHS[month - 1]}_${year}.pdf`);
      toast.success('PDF esportato!');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Errore durante l\'esportazione PDF');
    } finally { setExporting(false); }
  };

  return (
    <div data-testid="calendar-page">
      <Toaster position="top-right" richColors />
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Calendario</h1>
          <p className="text-[#52525B] mt-1 text-sm">
            {calendarView === 'personal' ? 'Il tuo calendario personale' : 'Calendario del team'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-4">
          <div className="flex border border-[#E4E4E7]">
            <button onClick={() => setCalendarView('personal')}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${calendarView === 'personal' ? 'bg-[#0A0A0A] text-white' : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'}`}
              data-testid="view-personal-btn"><User size={14} weight="bold" /> <span className="hidden sm:inline">Personale</span></button>
            <button onClick={() => setCalendarView('team')}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${calendarView === 'team' ? 'bg-[#0A0A0A] text-white' : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'}`}
              data-testid="view-team-btn"><Users size={14} weight="bold" /> Team</button>
          </div>
          {calendarView === 'team' && teams.length > 0 && (
            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger className="w-36 md:w-48 border-[#E4E4E7] rounded-none text-xs" data-testid="team-select"><SelectValue placeholder="Team" /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all" className="rounded-none">Tutti i Team</SelectItem>
                {teams.map((team) => (<SelectItem key={team.id} value={team.id} className="rounded-none">{team.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          {calendarView === 'team' && (
            <div className="flex items-center gap-1">
              <div className="flex border border-[#E4E4E7]">
                <button onClick={() => setDisplayMode('calendar')}
                  className={`px-2 py-1.5 text-[10px] font-bold transition-colors ${displayMode === 'calendar' ? 'bg-[#0A0A0A] text-white' : 'bg-white text-[#52525B]'}`}
                  data-testid="display-calendar-btn">Cal</button>
                <button onClick={() => setDisplayMode('grid')}
                  className={`px-2 py-1.5 text-[10px] font-bold transition-colors ${displayMode === 'grid' ? 'bg-[#0A0A0A] text-white' : 'bg-white text-[#52525B]'}`}
                  data-testid="display-grid-btn">Grid</button>
              </div>
              {displayMode === 'grid' && (
                <Button onClick={exportGridPdf} variant="outline" disabled={exporting} size="sm"
                  className="rounded-none border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white px-2 py-1"
                  data-testid="export-pdf-btn">
                  <FilePdf size={14} weight="bold" />
                </Button>
              )}
            </div>
          )}
          <div className="flex items-center gap-1">
            <button onClick={() => setCurrentDate(new Date(year, month - 2, 1))} className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors" data-testid="prev-month-btn"><CaretLeft size={18} weight="bold" /></button>
            <div className="w-28 md:w-48 text-center"><span className="font-heading font-bold text-sm md:text-lg">{MONTHS[month - 1]} {year}</span></div>
            <button onClick={() => setCurrentDate(new Date(year, month, 1))} className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors" data-testid="next-month-btn"><CaretRight size={18} weight="bold" /></button>
          </div>
        </div>
      </div>

      {/* On-call / General tabs */}
      {calendarView === 'team' && (
        <div className="flex items-center gap-0 mb-4 border-b border-[#E4E4E7]">
          <button onClick={() => setCalendarTab('general')}
            className={`px-5 py-2 text-sm font-bold transition-colors border-b-2 ${calendarTab === 'general' ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}
            data-testid="tab-general">Generale</button>
          <button onClick={() => setCalendarTab('oncall')}
            className={`px-5 py-2 text-sm font-bold transition-colors border-b-2 flex items-center gap-1.5 ${calendarTab === 'oncall' ? 'border-[#FF2A00] text-[#FF2A00]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}
            data-testid="tab-oncall"><Phone size={14} weight="bold" /> Reperibilità</button>
        </div>
      )}

      {/* Legend */}
      {!(displayMode === 'grid' && calendarView === 'team') && (
        <div className="flex items-center gap-6 mb-6 pb-4 border-b border-[#E4E4E7] flex-wrap">
          {Object.entries(GRID_BG).map(([type, color]) => (
            <div key={type} className="flex items-center gap-2">
              <div className="w-4 h-4" style={{ backgroundColor: color }}></div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#52525B]">{TYPE_LABELS[type]}</span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-96"><div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
      ) : displayMode === 'grid' && calendarView === 'team' ? (
        /* ===== GRID VIEW (captured by html2canvas for PDF) ===== */
        (() => {
          const { allDays, groups, totalRotationGroups } = buildGridData();
          const getOnCallDisplay = (dayMeta) => {
            if (!dayMeta) return { color: GRID_BG.on_call, label: 'R' };
            const gNum = dayMeta.onCallGroup;
            if (gNum && totalRotationGroups > 1) {
              return { color: ON_CALL_COLORS[(gNum - 1) % ON_CALL_COLORS.length], label: `R${gNum}` };
            }
            if (gNum) return { color: ON_CALL_COLORS[(gNum - 1) % ON_CALL_COLORS.length], label: `R${gNum}` };
            return { color: GRID_BG.on_call, label: 'R' };
          };
          const renderUserRow = (u) => {
                const isTL = teamLeaderIds.has(u.id);
                return (
                  <tr key={u.id} className="border-b border-[#E4E4E7]">
                    <td className="py-2 px-2 text-[11px] font-medium bg-white border-r border-[#E4E4E7] truncate" style={{ width: 130, minWidth: 130 }}>
                      <span className="flex items-center gap-1">
                        {isTL && <Trophy size={12} weight="fill" className="text-[#F59E0B] flex-shrink-0" />}
                        <span className="truncate">{u.name}</span>
                      </span>
                    </td>
                    {allDays.map(d => {
                      const dt = new Date(year, month - 1, d);
                      const weekend = dt.getDay() === 0 || dt.getDay() === 6;
                      const types = u.days[d];
                      const clickHandler = () => openDayDetail(u.id, u.name, d);
                      if (!types && weekend) {
                        return <td key={d} className="py-2 px-1 text-center border-r border-[#E4E4E7] bg-[#F4F4F5]"></td>;
                      }
                      if (!types && !weekend) {
                        return (
                          <td key={d} className="py-2 px-1 text-center border-r border-[#E4E4E7] cursor-pointer hover:bg-[#F4F4F5]" onClick={clickHandler}>
                            <span className="inline-flex items-center justify-center w-7 h-7 text-[10px] font-black text-white leading-none" style={{ backgroundColor: GRID_BG.office }}>P</span>
                          </td>
                        );
                      }
                      const primary = getPrimaryType(types);
                      const secondary = types.filter(t => t !== primary);
                      const isOnCall = primary === 'on_call';
                      const onCallInfo = isOnCall ? getOnCallDisplay(u.dayMeta?.[d]) : null;
                      const bg = isOnCall ? onCallInfo.color : (GRID_BG[primary] || '#E4E4E7');
                      const letter = isOnCall ? onCallInfo.label : (GRID_SHORT[primary] || '?');
                      const tooltipText = types.map(t => GRID_LABELS[t] || t).join(' + ');
                      return (
                        <td key={d} className={`py-2 px-1 text-center border-r border-[#E4E4E7] cursor-pointer hover:opacity-80 ${weekend ? 'bg-[#F4F4F5]' : ''}`}
                          title={`${u.name}: ${tooltipText} - ${d}/${month}`} onClick={clickHandler}>
                          <div className="relative inline-flex items-center justify-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 text-[10px] font-black text-white leading-none"
                              style={{ backgroundColor: bg }}>{letter}</span>
                            {secondary.map((st, si) => (
                              <span key={si} className="absolute flex items-center justify-center w-3 h-3 text-[5px] font-black text-white leading-none rounded-sm"
                                style={{ backgroundColor: GRID_BG[st] || '#52525B', top: -3, right: -4 - si * 12 }}
                                title={GRID_LABELS[st] || st}>
                                {GRID_SHORT[st] || '?'}
                              </span>
                            ))}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
          };
          // Filter groups for on-call tab
          const displayGroups = calendarTab === 'oncall'
            ? groups.map(g => ({
                ...g,
                users: g.users.filter(u => Object.values(u.days).some(types => types && types.includes('on_call')))
              })).filter(g => g.users.length > 0)
            : groups;
          return (
        <div ref={gridRef} className="bg-white p-2">
          {/* Logo + legend inside PDF area */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-6 flex-wrap">
              {Object.entries(GRID_BG).map(([type, color]) => (
                <div key={type} className="flex items-center gap-1.5">
                  <div className="w-3.5 h-3.5" style={{ backgroundColor: color }}></div>
                  <span className="text-[10px] font-bold text-[#52525B]">{GRID_SHORT[type]} = {GRID_LABELS[type]}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col items-end flex-shrink-0">
              {logoBase64 && <img src={logoBase64} alt="DCO Planner" className="h-6 w-auto object-contain" />}
              <div className="flex justify-between mt-0.5" style={{ width: logoBase64 ? undefined : 80 }}>
                {'DCO PLANNER'.split('').map((ch, i) => (
                  <span key={i} className="text-[7px] font-bold text-[#0A0A0A]" style={{ fontFamily: 'system-ui' }}>{ch === ' ' ? '\u00A0' : ch}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="border border-[#E4E4E7] overflow-x-auto">
            <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr className="border-b-2 border-[#E4E4E7]">
                  <th className="py-2 px-2 text-left text-[10px] font-bold uppercase tracking-wider bg-white border-r border-[#E4E4E7]" style={{ width: 130, minWidth: 130 }}>Dipendente</th>
                  {allDays.map(d => {
                    const dt = new Date(year, month - 1, d);
                    const dayName = DAYS[dt.getDay() === 0 ? 6 : dt.getDay() - 1];
                    const weekend = dt.getDay() === 0 || dt.getDay() === 6;
                    return (
                      <th key={d} className={`py-1.5 px-1 text-center text-[9px] border-r border-[#E4E4E7] leading-tight ${weekend ? 'bg-[#F4F4F5]' : ''}`} style={{ minWidth: 34, maxWidth: 38 }}>
                        <span className="block text-[8px] text-[#A1A1AA] leading-none mb-0.5">{dayName}</span>
                        <span className="font-bold leading-none">{d}</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {displayGroups.map(g => (
                  <React.Fragment key={g.team_id}>
                    {/* Team header row */}
                    <tr>
                      <td colSpan={allDays.length + 1} className="py-1 px-3 bg-[#F4F4F5] border-b border-[#E4E4E7]">
                        <span className="text-[9px] font-bold text-[#52525B] uppercase tracking-wider">{g.team_name}</span>
                        <span className="text-[9px] text-[#A1A1AA] ml-1">({g.users.length})</span>
                      </td>
                    </tr>
                    {g.users.map(u => renderUserRow(u))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          );
        })()
      ) : (
        /* ===== CALENDAR VIEW ===== */
        <div className="border border-[#E4E4E7]">
          <div className="grid grid-cols-7 border-b-2 border-[#0A0A0A]">
            {DAYS.map((day) => (<div key={day} className="py-3 px-4 text-xs font-bold uppercase tracking-[0.1em] text-center border-r border-[#E4E4E7] last:border-r-0">{day}</div>))}
          </div>
          <div className="grid grid-cols-7">
            {calendarDays.map((cell, index) => {
              const daySchedules = getSchedulesForDay(cell.day);
              const weekend = isWeekend(cell.day);
              return (
                <div key={index}
                  className={`schedule-cell p-2 border-r border-b border-[#E4E4E7] last:border-r-0 min-h-[90px] ${
                    !cell.isCurrentMonth ? 'bg-[#FAFAFA]' : weekend ? 'bg-[#F4F4F5]' : 'bg-white'
                  }`}
                  data-testid={cell.day ? `calendar-day-${cell.day}` : undefined}>
                  {cell.day && (
                    <>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-semibold ${weekend ? 'text-[#A1A1AA]' : 'text-[#0A0A0A]'}`}>{cell.day}</span>
                        {!weekend && daySchedules.length > 0 && <span className="text-[9px] text-[#52525B]">{daySchedules.length}</span>}
                      </div>
                      {!weekend && (
                        <div className="space-y-0.5 max-h-[60px] overflow-y-auto">
                          {calendarView === 'personal' ? (
                            daySchedules.slice(0, 3).map((s, idx) => (
                              <div key={idx} className="flex items-center gap-1 text-[10px] font-medium group" title={TYPE_LABELS[s.schedule_type]}>
                                <ScheduleIcon type={s.schedule_type} /><span className="truncate">{TYPE_LABELS[s.schedule_type]}</span>
                                {s.schedule_type === 'on_call' && s.user_id === user?.id && (
                                  <button onClick={(e) => { e.stopPropagation(); openSwapDialog(s); }}
                                    className="hidden group-hover:inline-flex w-4 h-4 items-center justify-center text-[#FF2A00] hover:bg-[#FF2A00]/10 flex-shrink-0"
                                    title="Proponi scambio reperibilità" data-testid={`swap-btn-${s.id}`}>
                                    <ArrowsClockwise size={10} weight="bold" />
                                  </button>
                                )}
                              </div>
                            ))
                          ) : (
                            daySchedules.slice(0, 4).map((s, idx) => (
                              <div key={idx} className="flex items-center gap-1 text-[10px] font-medium truncate" title={`${s.user_name}: ${TYPE_LABELS[s.schedule_type]}`}>
                                <ScheduleIcon type={s.schedule_type} /><span className="truncate">{s.user_name?.split(' ')[0]}</span>
                              </div>
                            ))
                          )}
                          {daySchedules.length > (calendarView === 'personal' ? 3 : 4) && (
                            <span className="text-[10px] text-[#52525B]">+{daySchedules.length - (calendarView === 'personal' ? 3 : 4)} altri</span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Swap On-Call Dialog */}
      <Dialog open={isSwapOpen} onOpenChange={setIsSwapOpen}>
        <DialogContent className="sm:max-w-md rounded-none border border-[#FF2A00]">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
            <ArrowsClockwise size={20} weight="bold" className="text-[#FF2A00]" /> Scambio Reperibilità
          </DialogTitle></DialogHeader>
          {swapSchedule && (
            <div className="space-y-4 mt-4">
              <div className="bg-[#FF2A00]/5 border border-[#FF2A00]/30 p-3">
                <p className="text-xs font-bold text-[#FF2A00]">Turno da scambiare</p>
                <p className="text-sm font-mono mt-1">{swapSchedule.start_date} → {swapSchedule.end_date}</p>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Collega destinatario</Label>
                <Select value={swapTargetId} onValueChange={setSwapTargetId}>
                  <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="swap-target-select"><SelectValue placeholder="Seleziona collega" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-52">
                    {colleagues.map(u => <SelectItem key={u.id} value={u.id} className="rounded-none">{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#A1A1AA] mt-1">Vengono mostrati i colleghi della stessa Business Line</p>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Motivo (opzionale)</Label>
                <Input value={swapReason} onChange={e => setSwapReason(e.target.value)}
                  placeholder="Es: Impegno personale" className="rounded-none border-[#E4E4E7]" data-testid="swap-reason-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsSwapOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                <Button disabled={swapping || !swapTargetId} onClick={handleSwap}
                  className="flex-1 bg-[#FF2A00] text-white hover:bg-[#D92400] rounded-none" data-testid="confirm-swap-btn">
                  {swapping ? 'Invio...' : 'Proponi Scambio'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Day Detail Dialog */}
      <Dialog open={dayDetailOpen} onOpenChange={setDayDetailOpen}>
        <DialogContent className="sm:max-w-md rounded-none border border-[#0A0A0A]">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">
              {dayDetailUser?.name} — {dayDetailUser?.day}/{month}/{year}
            </DialogTitle>
          </DialogHeader>
          {dayDetailLoading ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
          ) : !dayDetailData || dayDetailData.length === 0 ? (
            <div className="py-6 text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 mb-2" style={{ backgroundColor: GRID_BG.office }}>
                <span className="text-white font-black text-sm">P</span>
              </div>
              <p className="text-sm text-[#52525B]">Presente in ufficio (default)</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dayDetailData.map((entry, i) => {
                const bg = GRID_BG[entry.schedule_type] || '#E4E4E7';
                const label = TYPE_LABELS[entry.schedule_type] || entry.schedule_type;
                return (
                  <div key={i} className="border border-[#E4E4E7] p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 text-[10px] font-black text-white" style={{ backgroundColor: bg }}>
                        {GRID_SHORT[entry.schedule_type] || '?'}
                      </span>
                      <span className="font-bold text-sm">{label}</span>
                      {entry.is_holiday && <span className="text-[10px] bg-[#F59E0B]/10 text-[#F59E0B] px-2 py-0.5 font-bold">FESTIVO</span>}
                    </div>
                    <div className="grid grid-cols-2 gap-y-1.5 text-xs text-[#52525B]">
                      {entry.time_type === 'hours' && entry.start_time && (
                        <>
                          <span className="font-medium text-[#0A0A0A]">Orario:</span>
                          <span>{entry.start_time} — {entry.end_time}</span>
                        </>
                      )}
                      {entry.hours && (
                        <>
                          <span className="font-medium text-[#0A0A0A]">Ore:</span>
                          <span>{entry.hours}h</span>
                        </>
                      )}
                      <span className="font-medium text-[#0A0A0A]">Stato:</span>
                      <span className={entry.status === 'approved' ? 'text-[#22C55E] font-bold' : entry.status === 'rejected' ? 'text-[#FF2A00] font-bold' : 'text-[#F59E0B] font-bold'}>
                        {entry.status === 'approved' ? 'Approvato' : entry.status === 'rejected' ? 'Rifiutato' : entry.status === 'pending' ? 'In attesa' : entry.status}
                      </span>
                      {entry.requested_at && (
                        <>
                          <span className="font-medium text-[#0A0A0A]">Richiesto il:</span>
                          <span>{new Date(entry.requested_at).toLocaleDateString('it-IT')}</span>
                        </>
                      )}
                      {entry.approved_by && (
                        <>
                          <span className="font-medium text-[#0A0A0A]">Approvato da:</span>
                          <span className="font-bold text-[#22C55E]">{entry.approved_by}</span>
                        </>
                      )}
                      {entry.approved_at && (
                        <>
                          <span className="font-medium text-[#0A0A0A]">Data approvazione:</span>
                          <span>{new Date(entry.approved_at).toLocaleDateString('it-IT')}</span>
                        </>
                      )}
                      {entry.created_by_name && (
                        <>
                          <span className="font-medium text-[#0A0A0A]">Creato da:</span>
                          <span>{entry.created_by_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
